import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import useTable from 'src/hooks/useTable';
import { isEmpty } from 'lodash';
import NoteDetailsModal from './NoteDetailsModal';

export type NotesDisplayColumn = {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  width?: string;
  render?: (value: any, row: any) => React.ReactNode;
};

export type NotesDisplayProps = {
  data: any[];
  columns: NotesDisplayColumn[];
  title?: string;
  titleButton?: string;
  onAddNote?: () => void;
  isAllowedToAdd?: boolean;
  emptyMessage?: string;
  maxRowsPerPage?: number;
};

export default function NotesDisplay({
  data = [],
  columns,
  title,
  titleButton,
  onAddNote,
  isAllowedToAdd = false,
  emptyMessage = 'No notes available',
  maxRowsPerPage = 10,
}: NotesDisplayProps) {
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: maxRowsPerPage,
  });

  const [selectedNote, setSelectedNote] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewDetails = (row: any) => {
    setSelectedNote(row);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedNote(null);
  };

  const renderCellContent = (column: NotesDisplayColumn, value: any, row: any) => {
    // If custom render function provided, use it
    if (column.render) {
      return column.render(value, row);
    }

    // Default rendering based on column type
    switch (column.id) {
      case 'text':
      case 'description':
        return (
          <Tooltip title={value || ''}>
            <Box sx={{ maxWidth: 200 }}>
              <Typography
                style={{ cursor: 'pointer' }}
                noWrap
                sx={{ fontSize: `0.75rem` }}
              >
                {value || ''}
              </Typography>
            </Box>
          </Tooltip>
        );
      
      case 'topic':
        return (
          <Typography
            style={{ cursor: 'pointer' }}
            noWrap
            sx={{ fontSize: `0.75rem` }}
          >
            {value || 'General Note'}
          </Typography>
        );
      
      case 'author':
        return (
          <Typography
            style={{ cursor: 'pointer' }}
            noWrap
            sx={{ fontSize: `0.75rem` }}
          >
            {value || 'N/A'}
          </Typography>
        );
      
      case 'type':
        return (
          <Typography
            style={{ cursor: 'pointer' }}
            noWrap
            sx={{ fontSize: `0.75rem` }}
          >
            {value || 'N/A'}
          </Typography>
        );
      
      case 'date':
        return (
          <Typography
            style={{ cursor: 'pointer' }}
            noWrap
            sx={{ fontSize: `0.75rem` }}
          >
            {value || 'N/A'}
          </Typography>
        );
      
      case 'medium':
        return (
          <Typography
            style={{ cursor: 'pointer' }}
            noWrap
            sx={{ fontSize: `0.75rem` }}
          >
            {value || 'N/A'}
          </Typography>
        );
      
      default:
        return (
          <Typography
            style={{ cursor: 'pointer' }}
            noWrap
            sx={{ fontSize: `0.75rem` }}
          >
            {value || ''}
          </Typography>
        );
    }
  };

  return (
    <Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell 
                  key={`col-${index}`}
                  align={column.align || 'left'}
                  sx={{ width: column.width }}
                >
                  {column.label}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ width: '80px' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          
          {!isEmpty(data) ? (
            <TableBody>
              {data
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any, index: number) => (
                  <TableRow 
                    key={`row-${index}`}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      }
                    }}
                  >
                    {columns.map((column, colIndex) => (
                      <TableCell
                        key={`cell-${index}-${colIndex}`}
                        align={column.align || 'left'}
                        sx={{ width: column.width }}
                        onClick={() => handleViewDetails(row)}
                      >
                        {renderCellContent(column, row[column.id], row)}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(row);
                          }}
                          aria-label="view note details"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell colSpan={columns.length + 1}>
                  <Typography variant="body2" align="center">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
      
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data ? data.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      </Box>

      <NoteDetailsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        note={selectedNote}
      />
    </Box>
  );
}
